/**
 * PostHog: qué ve el visitante (template + dual CTA + cascada geo).
 *
 * Super-properties en TODOS los eventos posteriores + evento one-shot
 * `visitor_experience_resolved` por sesión/pestaña.
 *
 * Fuentes: window.__TEMPLATE_CONFIG__ (worker SSR) + overrides del SPA
 * (template efectivo en Index, dual live del snapshot).
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { templateUsesVariantOnHome } from "@/lib/heroCtaCompatibleTemplates";
import {
  getPostHogSlackSessionPropertiesPostHog,
  getUserRegionForTracking,
  isPostHogConfigured,
} from "@/lib/posthogAnalytics";
import {
  getServerTemplateConfig,
  getWorkerGeoAnalyticsProps,
  type DualVariantConfig,
} from "@/lib/serverTemplateDetection";
import posthog from "posthog-js";

const RESOLVED_SENTINEL = "juaperrone.visitor_experience_resolved.v1";

/**
 * Claim síncrono en memoria: evita doble `visitor_experience_resolved` cuando
 * SessionIngress + Index (o StrictMode) corren en el mismo tick antes de que
 * sessionStorage se escriba. sessionStorage sigue siendo la barrera cross-nav
 * en la misma pestaña.
 */
let resolvedEventClaimed = false;
/** Fingerprint del último resolved capturado — force solo re-emite si cambia. */
let lastResolvedFingerprint: string | null = null;

function experienceResolvedFingerprint(
  props: Record<string, string | number | boolean>,
  opts?: VisitorExperienceRegisterOpts,
): string {
  return [
    String(props.experience_effective_template ?? opts?.effectiveTemplate ?? ""),
    String(props.dual_surface ?? opts?.dualSurface ?? ""),
    String(props.dual_variant_id ?? opts?.dualVariant?.id ?? ""),
    String(props.experience_cascade ?? ""),
    String(props.experience_template_source ?? ""),
    typeof window !== "undefined" ? window.location.pathname : "",
  ].join("|");
}

export type VisitorExperienceRegisterOpts = {
  /** Template que Index/ruta está renderizando (puede diferir del worker seed). */
  effectiveTemplate?: string | null;
  /** Dual variant live (poll snapshot) — pisa el seed SSR si viene. */
  dualVariant?: DualVariantConfig | null;
  /** Surface dual del worker/snapshot. */
  dualSurface?: string | null;
  /** Ab bucket si el consumidor lo conoce aparte del cfg. */
  abBucket?: string | null;
};

function cleanProps(
  input: Record<string, string | number | boolean | null | undefined>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v == null || v === "") continue;
    out[k] = v;
  }
  return out;
}

/**
 * Resume geo cascade legible para breakdowns PostHog.
 * Ej: "city:denver|state_us:CO|country:US|dual_win:state_us:CO"
 */
export function buildExperienceCascadeKey(cfg: {
  country?: string | null;
  usState?: string | null;
  province?: string | null;
  city?: string | null;
  dualScope?: string | null;
  dualGeo?: string | null;
}): string {
  const parts: string[] = [];
  const country = (cfg.country || "").trim().toUpperCase();
  const city = (cfg.city || "").trim().toLowerCase();
  const usState = (cfg.usState || "").trim().toUpperCase();
  const province = (cfg.province || "").trim();
  if (city) parts.push(`city:${city}`);
  if (country === "AR" && province) parts.push(`province_ar:${province}`);
  if (country === "US" && usState) parts.push(`state_us:${usState}`);
  if (country && country !== "UNKNOWN") parts.push(`country:${country}`);
  if (cfg.dualScope && cfg.dualGeo) {
    parts.push(`dual_win:${cfg.dualScope}:${cfg.dualGeo}`);
  }
  return parts.join("|") || "unknown";
}

/**
 * Registra super-props de experiencia (template + dual + geo) en PostHog.
 * Llamar en boot, Index (template efectivo) y cuando live dual cambia.
 */
export function registerVisitorExperienceContextPostHog(
  opts?: VisitorExperienceRegisterOpts,
): Record<string, string | number | boolean> {
  if (!isPostHogConfigured()) return {};

  const cfg = getServerTemplateConfig();
  const workerTemplate = cfg?.templateName?.trim() || "";
  const effectiveTemplate =
    (opts?.effectiveTemplate && opts.effectiveTemplate.trim()) ||
    workerTemplate ||
    "";
  const dv =
    opts?.dualVariant !== undefined ? opts.dualVariant : (cfg?.dualVariant ?? null);
  const abBucket = opts?.abBucket ?? cfg?.abBucket ?? null;
  // Prefer explicit opt; fall back to worker seed so Index/SessionIngress get
  // dual_surface without every caller passing it.
  const dualSurface =
    opts?.dualSurface !== undefined && opts.dualSurface !== null
      ? opts.dualSurface
      : (cfg?.dualSurface ?? null);

  const cascade = buildExperienceCascadeKey({
    country: cfg?.country,
    usState: cfg?.usState,
    province: cfg?.province,
    city: cfg?.city,
    dualScope: dv?.assignmentScope,
    dualGeo: dv?.assignmentGeo,
  });

  const usesVariantHome = effectiveTemplate
    ? templateUsesVariantOnHome(effectiveTemplate)
    : false;

  const props = cleanProps({
    site: BRAND_SOURCE_KEY,
    brand_source_key: BRAND_SOURCE_KEY,

    // ── Template ──────────────────────────────────────────────────────────
    experience_worker_template: workerTemplate || undefined,
    experience_effective_template: effectiveTemplate || undefined,
    experience_template_source: cfg?.templateSource || undefined,
    experience_uses_variant_on_home: usesVariantHome,
    worker_template: workerTemplate || undefined,
    worker_template_source: cfg?.templateSource || undefined,
    effective_template: effectiveTemplate || undefined,

    // ── Dual variant ──────────────────────────────────────────────────────
    dual_has_variant: Boolean(dv?.id),
    dual_variant_id: dv?.id,
    dual_variant_display_name: dv?.displayName?.slice(0, 80),
    dual_variant_geo_scope: dv?.assignmentScope,
    dual_variant_geo_code: dv?.assignmentGeo,
    dual_variant_stripe_price_id: dv?.stripePriceId,
    dual_variant_price_usd:
      dv?.priceUsd != null && dv.priceUsd > 0 ? dv.priceUsd : undefined,
    dual_variant_mux_present: Boolean(dv?.muxPlaybackId),
    dual_variant_mp_label: dv?.mpVariantLabel,
    dual_ab_bucket: abBucket ? String(abBucket).slice(0, 64) : undefined,
    dual_surface: dualSurface || undefined,
    mux_playback_id: dv?.muxPlaybackId ? dv.muxPlaybackId.slice(0, 48) : undefined,

    // ── Hero / flags ──────────────────────────────────────────────────────
    hero_cta_type: cfg?.ctaType || undefined,
    experience_show_dual: cfg?.showDual === true,
    experience_hide_cta: cfg?.hideCta === true,
    experience_kill_switch: cfg?.killSwitchActive === true,

    // ── Geo cascade summary ───────────────────────────────────────────────
    experience_cascade: cascade,
    experience_country:
      cfg?.country && cfg.country !== "UNKNOWN" ? cfg.country : undefined,
    experience_us_state: cfg?.usState || undefined,
    experience_province: cfg?.province || undefined,
    experience_city: cfg?.city || undefined,
  });

  try {
    posthog.register(props);
  } catch {
    return props;
  }

  // Person profile: última experiencia vista (Persons / cohorts).
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const people = (posthog as any).people;
    if (people && typeof people.set === "function") {
      people.set(
        cleanProps({
          last_experience_template: effectiveTemplate || undefined,
          last_experience_template_source: cfg?.templateSource || undefined,
          last_dual_variant_id: dv?.id,
          last_dual_variant_geo: dv?.assignmentScope
            ? `${dv.assignmentScope}:${dv.assignmentGeo || ""}`
            : undefined,
          last_experience_us_state: cfg?.usState || undefined,
          last_experience_country:
            cfg?.country && cfg.country !== "UNKNOWN" ? cfg.country : undefined,
          last_experience_cascade: cascade,
        }),
      );
    }
  } catch {
    /* best-effort */
  }

  return props;
}

/**
 * Evento one-shot por pestaña: detalle completo de lo que se resolvió al entrar.
 * Idempotente vía claim en memoria + sessionStorage.
 *
 * `force: true` — permite promover el snapshot cuando el template/dual efectivo
 * difiere del primer fire (Index / DualCTA). Solo re-captura si el fingerprint
 * cambió; super-props se actualizan siempre vía register.
 */
export function trackVisitorExperienceResolvedPostHog(
  opts?: VisitorExperienceRegisterOpts & { force?: boolean },
): void {
  if (!isPostHogConfigured() || typeof window === "undefined") return;

  const force = opts?.force === true;

  if (!force) {
    // Claim atómico síncrono ANTES de cualquier trabajo: evita race entre
    // SessionIngressTracker e Index en el mismo frame.
    if (resolvedEventClaimed) return;
    try {
      if (sessionStorage.getItem(RESOLVED_SENTINEL) === "1") {
        resolvedEventClaimed = true;
        return;
      }
    } catch {
      /* private mode — solo claim en memoria */
    }
    resolvedEventClaimed = true;
  }

  const props = registerVisitorExperienceContextPostHog(opts);
  if (Object.keys(props).length === 0 && !getServerTemplateConfig()) {
    // No contamos como fired si no había nada útil; liberamos claim solo si
    // no era force (force ya asume re-emit consciente).
    if (!force) resolvedEventClaimed = false;
    return;
  }

  const fp = experienceResolvedFingerprint(props, opts);
  if (force && lastResolvedFingerprint !== null && fp === lastResolvedFingerprint) {
    // Same experience already captured — super-props already refreshed above.
    return;
  }
  if (!force && lastResolvedFingerprint === fp) {
    return;
  }

  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;
  const slack = getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true });

  try {
    posthog.capture("visitor_experience_resolved", {
      ...props,
      ...geoFlat,
      ...slack,
      page_path: window.location.pathname,
      page_url: window.location.href,
      user_region: getUserRegionForTracking(),
      timestamp: new Date().toISOString(),
      brand_source_key: BRAND_SOURCE_KEY,
      site: BRAND_SOURCE_KEY,
      experience_resolved_force: force || undefined,
    });
  } catch {
    if (!force) resolvedEventClaimed = false;
    return;
  }

  lastResolvedFingerprint = fp;
  resolvedEventClaimed = true;
  try {
    sessionStorage.setItem(RESOLVED_SENTINEL, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Re-register cuando el dual live (poll) cambia — no re-dispara resolved event.
 */
export function syncLiveDualExperiencePostHog(input: {
  dualVariant?: DualVariantConfig | null;
  dualSurface?: string | null;
  effectiveTemplate?: string | null;
}): void {
  registerVisitorExperienceContextPostHog({
    dualVariant: input.dualVariant,
    dualSurface: input.dualSurface,
    effectiveTemplate: input.effectiveTemplate,
  });
}
